import { ComponentProps, useId, ReactNode } from 'react';
import { VALIDITY } from '@/constants';
import styles from './checkbox.module.scss';
import { InvalidFeedback } from '../InvalidFeedback/InvalidFeedback';
import { CheckmarkSmallIcon } from '@/assets/icons/CheckmarkSmallIcon';

export interface Checkbox extends ComponentProps<'input'> {
    children: React.ReactNode | React.ReactNode[];
    ariaLabel?: string;
    invalidMessage?: ReactNode;
    invalid?: FieldValidityState;
    dataCy?: string;
    errorDataCy?: string;
}

export function Checkbox({
    id,
    invalid,
    ariaLabel,
    children,
    invalidMessage,
    onBlur,
    onChange,
    dataCy,
    errorDataCy,
    ...otherProps
}: Checkbox): React.JSX.Element {
    const generatedId = useId();
    const elementId = id ? id : generatedId;
    const errorId = `${elementId}-error`;
    const ariaInvalid = invalid === VALIDITY.invalid;

    function handleBlur(event: React.FocusEvent<HTMLInputElement, Element>) {
        if (onBlur) {
            onBlur(event);
        }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        if (onChange) {
            onChange(event);
        }
    }

    return (
        <>
            {invalid && invalidMessage && (
                <InvalidFeedback validState={invalid} errorId={errorId} dataCy={errorDataCy}>
                    {invalidMessage}
                </InvalidFeedback>
            )}
            <div className={styles.checkboxItem}>
                <input
                    {...otherProps}
                    className={styles.checkbox}
                    type="checkbox"
                    id={elementId}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-invalid={ariaInvalid}
                    aria-label={ariaLabel}
                    aria-describedby={errorId}
                    data-cy={dataCy}
                />
                <label htmlFor={elementId}>{children}</label>
                <CheckmarkSmallIcon />
            </div>
        </>
    );
}
